import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bell, Trash2, User, Save, AlertTriangle } from "lucide-react";

const PREF_LIST = [
  { k: "pickup_reminders", label: "Pickup reminders" },
  { k: "pickup_completed", label: "Pickup completed alerts" },
  { k: "missed_bag", label: "Missed bag alerts" },
  { k: "billing", label: "Billing reminders" },
  { k: "payment_failed", label: "Payment failed alerts" },
  { k: "weather_delay", label: "Weather delay alerts" },
  { k: "promotions", label: "Promotions & rewards" },
  { k: "referral_updates", label: "Referral updates" },
];

export default function SettingsPage() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({});
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || "", phone: user.phone || "" });
      const initial = {};
      PREF_LIST.forEach((p) => { initial[p.k] = user.notification_prefs?.[p.k] ?? true; });
      setPrefs(initial);
    }
  }, [user]);

  if (!user) return <div className="p-12 text-center text-slate-500">Please log in.</div>;

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/users/me/profile", profile);
      toast.success("Profile saved");
      await refreshMe();
    } catch { toast.error("Save failed"); }
    setSavingProfile(false);
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put("/users/me/prefs", { prefs });
      toast.success("Preferences saved");
      await refreshMe();
    } catch { toast.error("Save failed"); }
    setSavingPrefs(false);
  };

  const deleteAcct = async () => {
    if (deleteConfirm !== "DELETE") return toast.error("Type DELETE to confirm.");
    try {
      await api.delete("/users/me");
      toast.success("Account deleted. We'll miss you.");
      navigate("/", { replace: true });
      logout();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <h1 className="font-fredoka text-3xl font-bold text-slate-900" data-testid="settings-title">Settings</h1>

      <section className="card-clay p-6">
        <div className="flex items-center gap-2 mb-3"><User size={18}/><h2 className="font-fredoka font-bold text-lg">Profile</h2></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name"><input className="input-clay h-11 px-3 w-full" value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} data-testid="settings-name"/></Field>
          <Field label="Phone"><input className="input-clay h-11 px-3 w-full" value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} data-testid="settings-phone"/></Field>
          <Field label="Email (read-only)"><input className="input-clay h-11 px-3 w-full bg-slate-100" value={user.email} disabled/></Field>
        </div>
        <button onClick={saveProfile} disabled={savingProfile} className="btn-push-green mt-4 px-5 py-2.5 inline-flex items-center gap-2" data-testid="settings-save-profile"><Save size={14}/> {savingProfile ? "Saving…" : "Save profile"}</button>
      </section>

      <section className="card-clay p-6">
        <div className="flex items-center gap-2 mb-3"><Bell size={18}/><h2 className="font-fredoka font-bold text-lg">Notifications</h2></div>
        <p className="text-xs text-slate-500 mb-3">Important billing & safety alerts will still come through.</p>
        <div className="space-y-2" data-testid="settings-prefs">
          {PREF_LIST.map((p) => (
            <label key={p.k} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border-2 border-slate-100 cursor-pointer">
              <span className="text-sm font-semibold text-slate-700">{p.label}</span>
              <input type="checkbox" checked={!!prefs[p.k]} onChange={(e) => setPrefs({...prefs, [p.k]: e.target.checked})} className="w-5 h-5 accent-green-500" data-testid={`pref-${p.k}`}/>
            </label>
          ))}
        </div>
        <button onClick={savePrefs} disabled={savingPrefs} className="btn-push-green mt-4 px-5 py-2.5 inline-flex items-center gap-2" data-testid="settings-save-prefs"><Save size={14}/> {savingPrefs ? "Saving…" : "Save preferences"}</button>
      </section>

      <section className="card-clay p-6 border-red-200 bg-red-50">
        <div className="flex items-center gap-2 mb-3 text-red-900"><AlertTriangle size={18}/><h2 className="font-fredoka font-bold text-lg">Danger zone</h2></div>
        <p className="text-sm text-red-800 mb-3">Deleting your account is permanent. Some records may be kept for legal, tax, or fraud-prevention purposes.</p>
        <input className="input-clay h-11 px-3 w-full mb-2" placeholder="Type DELETE to confirm" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} data-testid="settings-delete-confirm"/>
        <button onClick={deleteAcct} className="btn-push-outline px-5 py-2.5 text-sm text-red-700 inline-flex items-center gap-2 border-red-300 bg-white" data-testid="settings-delete-btn">
          <Trash2 size={14}/> Delete my account
        </button>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
